import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters } from "@/lib/filters";

import { IrtCell } from "./Irt";
import { RefreshButton } from "./RefreshButton";
import { StockDetail } from "./StockDetail";
import { getStockTab, messageFor, STOCK_QUERY_KEY, type StockRow } from "./api";
import {
  deepLinkMissing, deepLinkTarget, filterRows, fmtRate, fmtUnits, impactLabel, inflightCards, inflightLabel, inflightLine, irtLine, irtShown, orderRows, rowKey, runBanner,
  runwaySentence, sourceLabel, statusWord, storeLabel, tilesFor, velocityWindow, xeroAsOf,
} from "./format";
import { useRefresh } from "./useRefresh";

const PAGE_SIZE = 50;

function options(values: string[]): Array<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: v }));
}

export function StockTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const filterKey = JSON.stringify(values);
  // null until the operator pages or filters: a deep link then chooses the first page shown.
  const [pageIndex, setPageIndex] = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [linkDismissed, setLinkDismissed] = useState(false);

  const query = useQuery({ queryKey: STOCK_QUERY_KEY, queryFn: getStockTab });
  const artifact = query.data?.data.artifact ?? null;
  const latestRun = query.data?.data.latest_run ?? null;
  const refresh = useRefresh(areaName, latestRun, query.data?.data.xero_refresh_configured === true);

  // parseFilters returns a new object on every render, so the memo keys on its content (filterKey).
  const rows = useMemo(() => orderRows(filterRows(artifact?.rows ?? [], values)), [artifact, filterKey]);
  // A Slack deep link (`?sku=`, `&store=`; C6 D3): the row it names opens with its detail and is marked in the table.
  const linked = deepLinkTarget(rows, search.sku, search.store, PAGE_SIZE);
  // A row the filters hide is still in the run: the sentence says so instead of "not in this run".
  const hiddenByFilters = !linked && deepLinkTarget(artifact?.rows ?? [], search.sku, search.store, PAGE_SIZE) != null;
  const linkMissing = artifact && !linked ? deepLinkMissing(search.sku, search.store, hiddenByFilters) : null;
  // A refetch or a narrower filter can leave the page past the end: clamp, never show an empty page.
  const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
  const page = Math.min(pageIndex ?? linked?.pageIndex ?? 0, lastPage);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const tiles = tilesFor(artifact?.rows ?? [], artifact?.run_date ?? null);
  const banner = runBanner(artifact, latestRun, { stalledRunId: refresh.stalledRunId });
  const xeroLine = xeroAsOf(artifact?.xero_read_at);
  // C2 v1.5: what the in-flight figures rest on; nothing while the engine has not read the Stock Movements DB.
  const inflightSummary = inflightLine(artifact?.segments.inflight);
  // C2 v1.7: what the IRT cross-check found this run; nothing on an artifact written before it.
  const irtSummary = irtLine(artifact?.segments.irt, artifact?.trigger_type);
  // While the step is not set up the engine writes `unchecked` on every row; the line alone says so (no per-row word).
  const irtOn = irtShown(artifact?.segments.irt);

  const filters: FilterSpec[] = [
    { name: "search", label: "Search SKU or product", kind: "text" },
    { name: "product", label: "Product", kind: "choice", options: options(artifact?.filters.products ?? []) },
    { name: "variant", label: "Variant", kind: "choice", options: options(artifact?.filters.variants ?? []) },
    { name: "location", label: "Location", kind: "choice", options: options(artifact?.filters.locations ?? []) },
    { name: "channel", label: "Channel", kind: "choice", options: options(artifact?.filters.channels ?? []) },
    {
      name: "store", label: "Store", kind: "choice",
      options: (artifact?.filters.channel_locations ?? []).map((cl) => {
        const row = artifact?.rows.find((r) => r.channel_location === cl);
        return { value: cl, label: row ? storeLabel(row) : cl };
      }),
    },
  ];

  const columns: ColumnDef<StockRow, unknown>[] = [
    {
      id: "sku", header: "SKU",
      cell: ({ row }) => (
        <button type="button" data-nonmutating className="text-left text-ks-ink underline-offset-2 hover:underline"
          onClick={() => setSelected(rowKey(row.original))}>
          <span className="font-medium">{row.original.sku}</span>
          <span className="block text-xs text-muted-foreground">{row.original.name}</span>
        </button>
      ),
    },
    { id: "store", header: "Store", cell: ({ row }) => storeLabel(row.original) },
    {
      id: "units", header: "Units",
      cell: ({ row }) => (
        <span>
          {fmtUnits(row.original.units)}{" "}
          <Badge variant="outline">{sourceLabel(row.original)}</Badge>
          {row.original.velocity_stale ? <Badge variant="outline">velocity from previous run</Badge> : null}
        </span>
      ),
    },
    {
      id: "rate", header: "Sales/day",
      cell: ({ row }) => {
        const w = velocityWindow(row.original);
        return `${fmtRate(row.original.velocity_used)}${w ? ` (${w})` : ""}`;
      },
    },
    { id: "days", header: "Days of stock", cell: ({ row }) => runwaySentence(row.original) },
    { id: "inbound", header: "+Inbound", cell: ({ row }) => impactLabel(row.original.inbound_units, row.original.days_after_inbound) },
    { id: "convert", header: "+Convert", cell: ({ row }) => impactLabel(row.original.convertible_units, row.original.days_added) },
    // C2 v1.5 (C6 release 2 debt D1 closed): units arriving on and leaving this row by conversions still in flight.
    { id: "inflight", header: "In flight", cell: ({ row }) => inflightLabel(row.original) },
    {
      id: "outage", header: "Outage",
      cell: ({ row }) => (
        <span>
          {row.original.outage_date ?? ""}
          {row.original.final_outage_date && row.original.final_outage_date !== row.original.outage_date ? (
            <span className="block text-xs text-muted-foreground">final {row.original.final_outage_date}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: "status", header: "Status",
      cell: ({ row }) => (
        <span>
          <Badge variant={row.original.status === "below_min" || row.original.status === "unknown" ? "destructive" : "secondary"}>
            {statusWord(row.original.status)}
          </Badge>
          {/* C2 v1.7: an uncovered stock-out reads NEW ISSUE under its status, a covered one links its issue (no tenth column). */}
          <IrtCell verdict={irtOn ? row.original.irt : undefined} />
        </span>
      ),
    },
  ];

  const selectedKey = selected ?? (linkDismissed ? null : linked?.key ?? null);
  const selectedRow = artifact?.rows.find((r) => rowKey(r) === selectedKey) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {banner.kind !== "ok" ? (
          <Alert variant={banner.kind === "failed" ? "destructive" : "default"} className="max-w-3xl">
            <AlertTitle>{banner.text}</AlertTitle>
            {banner.kind === "partial" ? <AlertDescription>Rows from the missing source show as Unknown.</AlertDescription> : null}
          </Alert>
        ) : <div />}
        <RefreshButton
          state={refresh.state}
          message={refresh.message}
          progress={refresh.progress}
          xeroAvailable={refresh.xeroAvailable}
          onStart={(source) => void refresh.start(source)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["SKU x store", tiles.skus],
          ["Below minimum", tiles.belowMin],
          ["Unknown", tiles.unknown],
          ["Outages within 30 days", tiles.outagesSoon],
        ].map(([label, value]) => (
          <Card key={String(label)}>
            <CardHeader className="pb-1"><CardTitle className="text-sm font-normal">{label}</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold">{value}</CardContent>
          </Card>
        ))}
      </div>

      {xeroLine ? <p className="text-sm text-muted-foreground">{xeroLine}</p> : null}
      {inflightSummary ? <p className="text-sm text-muted-foreground">{inflightSummary}</p> : null}
      {irtSummary ? <p className="text-sm text-muted-foreground">{irtSummary}</p> : null}
      {linkMissing ? <p role="status" className="text-sm text-ks-ink">{linkMissing}</p> : null}

      <div>
          <FilterBar
            filters={filters}
            values={values}
            onChange={(next) => {
              setPageIndex(0);
              const navigateOptions: Record<string, unknown> = {
                search: (prev: Record<string, unknown>) => applyFilters(prev, next),
                replace: true,
              };
              void navigate(navigateOptions as never);
            }}
          />
          {query.isError ? (
            <p role="alert" className="text-sm text-ks-danger">{messageFor(query.error, areaName)}</p>
          ) : (
            <DataTable
              columns={columns}
              rows={pageRows}
              page={{ pageIndex: page, pageSize: PAGE_SIZE, rowCount: rows.length }}
              onPageChange={setPageIndex}
              rowClassName={(r) => (linked && rowKey(r) === linked.key ? "ring-2 ring-inset ring-ks-purple" : undefined)}
              pending={query.isLoading}
              emptyMessage={artifact ? "No rows match these filters." : "No run has completed yet."}
            />
          )}
          <p className="mt-2 text-sm text-muted-foreground">Select a SKU to see both velocity windows, the stock reading and the threshold source.</p>
      </div>
      <StockDetail
        row={selectedRow}
        cards={selectedRow ? inflightCards(artifact?.segments.inflight, selectedRow) : []}
        irt={irtOn ? selectedRow?.irt : undefined}
        onClose={() => { setSelected(null); setLinkDismissed(true); }}
      />
    </div>
  );
}
